// academyComposer — utilities shared between the "New course" and
// "Edit course" admin pages.
//
// Responsibilities:
//   1. Auto-suggest linked fiches (appellations · cépages · producers)
//      from a free-text title/theme by matching region + appellation
//      keywords. The operator remains the final judge — they can
//      always add/remove suggestions in the picker.
//   2. Build the mandatory 10-section structure preamble that we
//      append to `extra_instructions` before shipping to upstream so
//      the LLM produces rich, geovinum-branded courses instead of
//      the current 3-block skeletons.
//   3. Auto-suggest the matching shop product (map to buy at the end
//      of the certification) from the course region.
//
// All functions are pure — no fetches, no state — so they're safe to
// call from any React component or a unit test.

const norm = (s) => (s || "")
  .toString()
  .toLowerCase()
  .normalize("NFD")
  .replace(/[\u0300-\u036f]/g, "") // strip diacritics
  .trim();

/**
 * Auto-suggest linked fiches from a free-text seed.
 *
 * Matching rules (in priority order):
 *   1. Appellation name/slug appears in seed → link the appellation
 *   2. Appellation's region matches a token in seed → link the appellation
 *   3. Cépage name appears in seed → link the cépage
 *   4. Cépage appears in a linked appellation's `cepages` list → link it
 *   5. Producer's region matches a linked appellation's region → link (top 5)
 *   6. Producer's name/appellation appears in seed → link
 *
 * Returns { appellations: Set<slug>, cepages: Set<slug>, producers: Set<slug> }.
 */
export function suggestLinkedFiches({ seed, atlas }) {
  const out = { appellations: new Set(), cepages: new Set(), producers: new Set() };
  if (!seed || !atlas) return out;
  const n = norm(seed);
  if (!n) return out;
  // Tokenise on whitespace + punctuation for word-level matching.
  const tokens = new Set(n.split(/[^a-z0-9]+/).filter((t) => t.length >= 3));

  const appellations = atlas.appellations || [];
  const cepages = atlas.cepages || [];
  const producers = atlas.producers || [];

  // Step 1-2 · Appellations by direct name or region match.
  const linkedRegions = new Set();
  for (const a of appellations) {
    const name = norm(a.name);
    const slug = norm(a.slug);
    const region = norm(a.region);
    const nameMatch = name && (n.includes(name) || tokens.has(name.split(" ")[0]));
    const slugMatch = slug && n.includes(slug);
    const regionMatch = region && (n.includes(region) || tokens.has(region.split(" ")[0]));
    if (nameMatch || slugMatch || regionMatch) {
      if (a.slug) out.appellations.add(a.slug);
      if (a.region) linkedRegions.add(norm(a.region));
    }
  }

  // Step 3 · Cépages by direct name match.
  for (const c of cepages) {
    const name = norm(c.name);
    if (name && n.includes(name)) {
      if (c.slug) out.cepages.add(c.slug);
    }
  }

  // Step 4 · Cépages listed on any linked appellation.
  for (const a of appellations) {
    if (!a.slug || !out.appellations.has(a.slug)) continue;
    const list = a.cepages || a.grape_varieties || [];
    for (const gv of list) {
      const gvName = typeof gv === "string" ? gv : (gv.name || gv.slug || "");
      const gvNorm = norm(gvName);
      if (!gvNorm) continue;
      const match = cepages.find((c) => norm(c.name) === gvNorm || norm(c.slug) === gvNorm);
      if (match?.slug) out.cepages.add(match.slug);
    }
  }

  // Step 5-6 · Producers by region + direct match. Cap at 8 to keep the
  // pre-selection focused; the operator can always add more.
  const prodCandidates = [];
  for (const p of producers) {
    if (!p.slug) continue;
    const pName = norm(p.name);
    const pRegion = norm(p.region);
    const pApp = norm(p.appellation || p.main_appellation);
    if (pName && n.includes(pName)) prodCandidates.push([p, 10]); // strong: name in seed
    else if (pApp && out.appellations.has(pApp)) prodCandidates.push([p, 7]); // in linked appellation
    else if (pRegion && linkedRegions.has(pRegion)) prodCandidates.push([p, 5]); // in region
  }
  prodCandidates.sort((a, b) => b[1] - a[1]);
  for (const [p] of prodCandidates.slice(0, 8)) {
    out.producers.add(p.slug);
  }

  return out;
}

/**
 * Suggest a shop product matching the course region.
 * Returns the product_id (or null if no clear match).
 *
 * Match ranking (strongest first):
 *   1. Any linked appellation's slug appears in the product title
 *   2. Any linked appellation's region appears in the product title
 *   3. region_slug/label matches a linked appellation's region
 *   4. region_slug/label appears as a token in the free-text seed
 */
export function suggestShopProduct({ seed, atlas, linkedAppellations, products }) {
  if (!products || !products.length) return null;
  const seedNorm = norm(seed);
  const appellations = atlas?.appellations || [];
  const linkedSlugs = new Set((linkedAppellations || []).map((s) => norm(s)));
  const linkedRegions = new Set();
  for (const slug of linkedSlugs) {
    const a = appellations.find((x) => norm(x.slug) === slug);
    if (a?.region) linkedRegions.add(norm(a.region));
  }

  // 1) Any linked appellation slug appears in the product title.
  for (const p of products) {
    const t = norm(p.title);
    if (!t) continue;
    for (const slug of linkedSlugs) {
      // Only match slugs long enough to be meaningful (avoids "ahr" et al.)
      if (slug.length < 5) continue;
      const bare = slug.replace(/-/g, " ");
      if (t.includes(bare) || t.includes(slug)) return p.product_id || p.id;
    }
  }

  // 2) Any linked appellation region appears in the product title.
  for (const p of products) {
    const t = norm(p.title);
    if (!t) continue;
    for (const region of linkedRegions) {
      if (region.length < 4) continue;
      if (t.includes(region)) return p.product_id || p.id;
    }
  }

  // 3) region_slug / region_label match linked region.
  for (const p of products) {
    const rSlug = norm(p.region_slug);
    const rLabel = norm(p.region_label);
    if (rSlug && (linkedRegions.has(rSlug) || linkedRegions.has(rSlug.replace(/-/g, " ")))) {
      return p.product_id || p.id;
    }
    if (rLabel && linkedRegions.has(rLabel)) {
      return p.product_id || p.id;
    }
  }

  // 4) region_slug / region_label present as token in seed.
  for (const p of products) {
    const rSlug = norm(p.region_slug);
    const rLabel = norm(p.region_label);
    if (rSlug && seedNorm.includes(rSlug.replace(/-/g, " "))) return p.product_id || p.id;
    if (rLabel && seedNorm.includes(rLabel)) return p.product_id || p.id;
  }

  return null;
}

/**
 * Build the mandatory course-structure preamble that we append to
 * `extra_instructions` before shipping to the upstream LLM. This is
 * how we enforce a rich 10-section course without needing an upstream
 * code change — the upstream generator reads `extra_instructions` and
 * follows the instructions verbatim.
 *
 * `context` carries the resolved fiche names + shop product info so
 * the LLM can produce the exact iframe URL and buy-the-map CTA.
 */
export function buildStructurePreamble({
  regionSlug,
  regionLabel,
  linkedAppellationNames = [],
  linkedCepageNames = [],
  linkedProducerNames = [],
  shopProduct = null,
  iframeUrl = "",
} = {}) {
  const app = linkedAppellationNames.length
    ? linkedAppellationNames.slice(0, 12).join(", ")
    : "les appellations fournies";
  const cep = linkedCepageNames.length
    ? linkedCepageNames.slice(0, 8).join(", ")
    : "les cépages emblématiques de la région";
  const prod = linkedProducerNames.length
    ? linkedProducerNames.slice(0, 8).join(", ")
    : "les producteurs incontournables";
  const productLine = shopProduct
    ? `- URL Shop : https://geovinum.io/shop/${shopProduct.product_id || shopProduct.id}\n  - Titre carte : ${shopProduct.title}\n  - Prix à partir de : ${(shopProduct.from_amount_cents || 0) / 100} ${shopProduct.currency || "EUR"}`
    : "- (aucune carte associée automatiquement — laisser un placeholder « Découvrez notre carte dédiée sur geovinum.io/shop »)";
  const iframeLine = iframeUrl
    ? `URL iframe carte : ${iframeUrl}`
    : "URL iframe : à omettre si la région n'a pas d'iframe disponible (utiliser un bloc image à la place)";

  return `

=== STRUCTURE OBLIGATOIRE DU PARCOURS (10 SECTIONS) ===
Le parcours DOIT suivre CETTE structure exacte, dans CET ordre. Produis chaque section comme un bloc dédié dans \`blocks[]\`. Aucune section ne peut être omise même si l'information est partielle — sois exhaustif, pédagogique et concret.

BLOC 1 · TITRE + INTRODUCTION (type: "text")
- Sous-titre engageant
- 2-3 paragraphes d'introduction expliquant CE QUE le parcours va couvrir
- Une liste à puces claire : « À la fin de ce parcours vous saurez… »
- Estimation de durée + niveau (WSET 2/3)

BLOC 2 · HISTOIRE & CULTURE DE LA RÉGION (type: "text")
- Ancrage historique : origines viticoles, dates-clés, événements majeurs (phylloxéra, appellations reconnues…)
- Éléments culturels : gastronomie locale, patrimoine, événements
- 400-600 mots, ton narratif

BLOC 3 · GÉOGRAPHIE & TERROIR (type: "text")
- Situation géographique précise (latitude, altitude, cours d'eau, orientations)
- Climat : type (continental / océanique / méditerranéen), précipitations, amplitudes
- Sols et sous-sols : détail géologique (calcaires, argiles, granites, schistes…)
- Impact du terroir sur le style des vins
- 500-700 mots

BLOC 4 · CARTE INTERACTIVE (type: "iframe")
- ${iframeLine}
- Titre au-dessus : « Explorez la région sur la carte interactive »
- Contenu du bloc : { "type": "iframe", "url": "…", "title": "Carte de ${regionLabel || "la région"}", "height": 500 }

BLOC 5 · ÉCONOMIE & FILIÈRE (type: "text")
- Superficies plantées, nombre de domaines, volumes produits
- Rendements moyens, exports, marchés-clés
- Interprofession(s) et syndicats
- Enjeux environnementaux : bio, biodynamie, HVE, initiatives collectives
- 300-500 mots

BLOC 6 · CÉPAGES À CONNAÎTRE (type: "text")
- Cépages principaux : ${cep}
- Pour chaque cépage : origine, caractère (arômes, structure), rôle dans les assemblages ou monocépages
- Tableau ou liste à puces claire

BLOC 7 · APPELLATIONS (type: "text")
- Panorama des appellations : ${app}
- Hiérarchie (régionale / villages / crus / grands crus)
- Cahier des charges essentiels : cépages autorisés, rendements max, élevage min
- Styles de vin produits

BLOC 8 · PRODUCTEURS INCONTOURNABLES (type: "text")
- 4-8 producteurs incontournables : ${prod}
- Pour chaque : positionnement (traditionnel/moderne/bio), cuvées phares, prix indicatifs
- Adresse et lien vers leur fiche geovinum.io/producteur/{slug}

BLOC 9 · EXERCICES (types: "flashcards", "quiz", "challenge")
- 10-15 flashcards (recto question / verso réponse détaillée) couvrant terroir + appellations + cépages
- Quiz final QCM 8-12 questions (1 seule bonne réponse + justification)
- 1 challenge de dégustation à l'aveugle avec grille de notation

BLOC 10 · CERTIFICATION & CARTE À OFFRIR (type: "certification")
- Message de félicitations personnalisé
- CTA « Achetez la carte des vins de ${regionLabel || "la région"} pour aller plus loin » :
${productLine}
- Badge : { "level": "explorer" }

=== EXIGENCES DE QUALITÉ ===
- LONGUEUR MINIMALE : chaque bloc texte fait au minimum 300 mots (sauf indication contraire). Rejette toute réponse qui produit des blocs de moins de 200 mots.
- FAITS PRÉCIS : cite des chiffres (superficie en hectares, altitude en m, rendement en hl/ha, prix en €), des dates, des noms de lieux ET de personnes.
- MAILLAGE INTERNE : mentionne les fiches liées avec leur slug entre crochets [appellation:beaujolais], [cepage:gamay], [producteur:{slug}] pour que le renderer puisse créer des liens automatiques.
- LANGUE : produis EN FRANÇAIS, ton pédagogique et accessible (niveau WSET 2/3), sans jargon inutile.
- SORTIE : JSON strict avec le tableau \`blocks\` — chaque bloc a \`type\`, \`title\` (optionnel), \`body\` ou \`items\` selon le type.
`.trim();
}

/**
 * Build an iframe URL for a given region slug.
 * We use the same pattern as the public Explorer pages so the same
 * embed used on the site is reused inside courses.
 */
export function buildRegionIframeUrl(regionSlug) {
  if (!regionSlug) return "";
  return `https://geovinum.io/embed/region/${regionSlug}`;
}
